import { Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { trackAvailableStoreViewed } from '@/features/shared/analytics/selectStore'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import {
  type CMSProductShelfInnerData,
  type StoreCardForProductShelf,
} from '@/features/cms/components/CMS/types'
import { ArrowRightIcon } from '@shipt/design-system-icons'
import { StoreAttribute } from '@/features/cms/components/CMS/components/BrandAmplification/StoreAttribute'
import { Fragment } from 'react'
import { Avatar } from '@shipt/design-system-avatar'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { calcRem, screenSizes } from '@shipt/design-system-themes'
import { IconButtonLink, TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { InfoButton } from '@/features/shared/components/InfoButton'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicStoresCarouselDisclaimerDrawer } from '@/features/cms/components/CMS/components/BrandAmplification/StoresCarouselDisclaimerDrawer/DynamicStoresCarouselDisclaimerDrawer'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  index: number
  data: CMSProductShelfInnerData & {
    store_card: StoreCardForProductShelf
  }
  handleStoreClick: () => void
  viewAllHref?: string
}

const BrandAmplificationButtons = ({
  storeName,
  handleStoreClick,
  viewAllHref,
}: {
  storeName: string
  handleStoreClick: () => void
  viewAllHref?: string
}) => {
  if (!viewAllHref) return null

  return (
    <>
      <Row display={{ base: 'none', md: 'flex' }}>
        <ViewAllTextButtonLink
          href={viewAllHref}
          onClick={(e) => {
            e.preventDefault()
            handleStoreClick()
          }}
          aria-label={`View all for ${storeName}`}
        >
          View all
        </ViewAllTextButtonLink>
      </Row>

      <Row display={{ base: 'flex', md: 'none' }} align="center">
        <IconButtonLink
          href={viewAllHref}
          onClick={(e) => {
            e.preventDefault()
            handleStoreClick()
          }}
          aria-label={`View all for ${storeName}`}
          size="sm"
          variant="tertiary"
          icon={ArrowRightIcon}
        />
      </Row>
    </>
  )
}

export const RefreshShelfHeading = ({ index, data, handleStoreClick, viewAllHref }: Props) => {
  const { openDialog } = useHandleDialog()
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const { disclaimer, header_variant, store_card, subheading } = data
  const {
    primary_attributes,
    secondary_attributes,
    background_color,
    retailer_id: storeId,
    name: storeName,
    logo: { src: storeLogo },
  } = store_card.data
  const heading = !data.web_image ? data.heading : undefined
  const isCompact = header_variant === 'compact'

  const primaryAttributes = primary_attributes?.slice(0, 2) ?? null
  const secondaryAttribute = secondary_attributes?.[0] ?? null

  const handleOpenDisclaimer = () => {
    trackCMSElementClicked({ content: 'info_icon' })
    openDialog(DynamicStoresCarouselDisclaimerDrawer, {
      title: disclaimer?.data.heading ?? '',
      content: disclaimer?.data.content?.elements ?? [],
    })
  }

  return (
    <Row justify="space-between" marginBottom="sm">
      <ScrollIntoView
        onInView={() =>
          trackAvailableStoreViewed({
            storeId,
            storeName,
            index,
          })
        }
      >
        <Row spacing="sm" align="center">
          <RetailerAvatar
            type="image"
            colorScheme="retailer"
            retailerColor={background_color ?? ''}
            size="lg"
            image={
              <RectangularImage
                src={storeLogo}
                alt="rectangular store logo"
                width={56}
                height={40}
              />
            }
          />
          <Column>
            {!isCompact && !!heading && (
              <Body size={{ base: 'sm', md: 'md' }} variant="secondary">
                {storeName}
              </Body>
            )}
            <Body strong size={isCompact ? { base: 'xl', md: 'xxl' } : { base: 'md', md: 'lg' }}>
              {heading ?? storeName}
            </Body>
            {!!heading && !!subheading && (
              <Subheading>
                {subheading.elements.map(renderElement)}
                {!!disclaimer?.data.heading && !!disclaimer.data.content?.elements && (
                  <InfoButton aria-label="more information" onClick={handleOpenDisclaimer} />
                )}
              </Subheading>
            )}
            {!heading && !!primaryAttributes && (
              <Row align="center" spacing="xs">
                {primaryAttributes.map((attribute, i) => (
                  <Fragment key={attribute.data.text}>
                    <StoreAttribute
                      astro_icon={attribute.data.astro_icon}
                      text={attribute.data.text}
                      treatment={attribute.data.treatment}
                    />
                    {!!(i < primaryAttributes.length - 1 || secondaryAttribute) && (
                      <span aria-hidden="true">{' \u2027 '}</span>
                    )}
                  </Fragment>
                ))}
                {!!secondaryAttribute && (
                  <Body size="sm" variant="secondary">
                    {secondaryAttribute.data.text}
                  </Body>
                )}
              </Row>
            )}
          </Column>
        </Row>
      </ScrollIntoView>
      <BrandAmplificationButtons
        storeName={storeName}
        handleStoreClick={handleStoreClick}
        viewAllHref={viewAllHref}
      />
    </Row>
  )
}

const Subheading = styled.div`
  p {
    color: ${theme.color.content.base.neutral.secondary};
    font-size: ${calcRem(theme.font.static.body.regular.compact.md.fontSize)}rem;

    @media ${screenSizes.tablet} {
      font-size: ${calcRem(theme.font.static.body.regular.compact.lg.fontSize)}rem;
    }
  }

  /* allow disclaimer to display inline with last paragraph */
  p:last-of-type {
    display: inline;
  }
`

const ViewAllTextButtonLink = styled(TextButtonLink)`
  flex-shrink: 0;
`

const RetailerAvatar = styled(Avatar)`
  flex-shrink: 0;
`

const RectangularImage = styled(Image)`
  object-fit: contain;
`
